import React, { useState, useEffect } from 'react';
import { DataProviderInstance } from '../../../../api/DataProvider';
import styles from './FiscalSettings.module.css';
import FiscalNfseLayoutSection from './FiscalNfseLayoutSection';
import Loading from '../../../../components/loading/Loading';
import AreaTitle from '../../../../components/areaTitle/AreaTitle';
import InputField from '../../../../components/inputfield/InputField';
import CustomButton from '../../../../components/buttons/CustomButton';
import ExternalIntegrationInfo from '../../../../components/external-integration-info/ExternalIntegrationInfo';
import useSubscriptionFeatures from '../../../../hooks/useSubscriptionFeatures';
import { SUBSCRIPTION_PERMISSIONS } from '../../../../consts/SubscriptionFeatures';
import FeatureLockBanner from '../../../../components/subscription/FeatureLockBanner';
import PlanUpgradeModal from '../../../../components/subscription/PlanUpgradeModal';
import { PROFESSIONAL_PLAN_NAME } from '../../../../components/subscription/planLockTexts';
import {
  Typography,
  Paper,
  Alert,
  Divider,
  Box,
  Switch,
  FormControlLabel,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
} from '@mui/material';
import ReceiptLongOutlinedIcon from '@mui/icons-material/ReceiptLongOutlined';
import SaveOutlinedIcon from '@mui/icons-material/SaveOutlined';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import CloseOutlinedIcon from '@mui/icons-material/CloseOutlined';
import WarningAmberOutlinedIcon from '@mui/icons-material/WarningAmberOutlined';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ColorPalette from '../../../../colorpalette/ColorPalette';
import { useRoleAccess } from '../../../../hooks/useRoleAccess';
import { USER_ROLES } from '../../../../constants/userRoles';

const EMPTY_FORM = {
  enabled: false,
  provider: 'focus_nfe',
  environment: 'homologacao',
  api_key: '',
  issuer_cnpj: '',
  issuer_legal_name: '',
  issuer_municipal_registration: '',
  issuer_city_code: '',
  issuer_city_name: '',
  default_service_code: '',
  default_service_description: '',
  default_aliquot: '',
  default_iss_withheld: false,
  send_email_to_patient: false,
  copy_email: '',
  // Campos da Reforma Tributária (NFS-e)
  // item_lista_servico: código do serviço na tabela da prefeitura (só layout municipal)
  item_lista_servico: '04391',
  codigo_nbs: '000000000',
  codigo_indicador_operacao: '000000',
  ibs_cbs_classificacao_tributaria: '000001',
  natureza_operacao: '1',
  // Layout de emissão e dados do Emissor Nacional (ver FiscalNfseLayoutSection).
  // O enquadramento substitui o antigo switch is_simples_nacional, derivado dele ao salvar.
  // Configuração nova começa no nacional: o enquadramento padrão (ME/EPP) é obrigado a ele desde 01/11/2026.
  nfse_layout: 'nacional',
  codigo_opcao_simples_nacional: 3,
  regime_tributario_simples_nacional: '',
  regime_especial_tributacao: 0,
  tributacao_iss: 1,
  codigo_tributacao_nacional_iss: '',
  ibs_cbs_informar: false,
  percentual_tributos_simples_nacional: '',
  dps_serie: '1',
  dps_proximo_numero: '1',
};

function FiscalSettings() {
  const dataProvider = DataProviderInstance();
  const { hasRole } = useRoleAccess();
  const canEdit = hasRole([USER_ROLES.COMPANY_OWNER, USER_ROLES.COMPANY_ADMIN]);
  const { hasPermission } = useSubscriptionFeatures();
  const [upgradeModalOpen, setUpgradeModalOpen] = useState(false);

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editing, setEditing] = useState(false);
  const [hasConfig, setHasConfig] = useState(false);
  const [hasApiKey, setHasApiKey] = useState(false);
  const [savedSettings, setSavedSettings] = useState(null);
  const [form, setForm] = useState({ ...EMPTY_FORM });
  const [errors, setErrors] = useState({});
  const [successMsg, setSuccessMsg] = useState('');
  const [errorMsg, setErrorMsg] = useState('');

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = () => {
    setLoading(true);
    dataProvider.GetFiscalSettings((result) => {
      setLoading(false);
      if (result?.success) {
        if (result.settings) {
          setHasConfig(true);
          setSavedSettings(result.settings);
          populateForm(result.settings);
        } else {
          setHasConfig(false);
          setSavedSettings(null);
          setForm({ ...EMPTY_FORM });
          // Inicia em modo view; usuário clica em Editar para configurar
          setEditing(false);
        }
      } else {
        setErrorMsg('Erro ao carregar configurações fiscais.');
      }
    });
  };

  const populateForm = (settings) => {
    setHasApiKey(settings.has_api_key === true);
    setForm({
      enabled: settings.enabled ?? false,
      provider: settings.provider ?? 'focus_nfe',
      environment: settings.environment ?? 'homologacao',
      api_key: '', // nunca pré-preencher a chave por segurança
      issuer_cnpj: settings.issuer_cnpj ?? '',
      issuer_legal_name: settings.issuer_legal_name ?? '',
      issuer_municipal_registration: settings.issuer_municipal_registration ?? '',
      issuer_city_code: settings.issuer_city_code ?? '',
      issuer_city_name: settings.issuer_city_name ?? '',
      default_service_code: settings.default_service_code ?? '',
      default_service_description: settings.default_service_description ?? '',
      default_aliquot: settings.default_aliquot != null ? String(settings.default_aliquot) : '',
      default_iss_withheld: settings.default_iss_withheld ?? false,
      send_email_to_patient: settings.send_email_to_patient ?? false,
      copy_email: settings.copy_email ?? '',
      // Campos da Reforma Tributária — sem fallback, valor vem do banco ou do EMPTY_FORM
      item_lista_servico: settings.item_lista_servico ?? '043901',
      codigo_nbs: settings.codigo_nbs ?? '000000000',
      codigo_indicador_operacao: settings.codigo_indicador_operacao ?? '000000',
      ibs_cbs_classificacao_tributaria: settings.ibs_cbs_classificacao_tributaria ?? '000001',
      natureza_operacao: settings.natureza_operacao ?? '1',
      // Layout de emissão e dados do Emissor Nacional. Registros antigos sem enquadramento
      // caem em ME/EPP optante (3) ou, se marcados como não optantes, em 1.
      nfse_layout: settings.nfse_layout ?? 'municipal',
      codigo_opcao_simples_nacional: settings.codigo_opcao_simples_nacional ?? (settings.is_simples_nacional === false ? 1 : 3),
      regime_tributario_simples_nacional: settings.regime_tributario_simples_nacional ?? '',
      regime_especial_tributacao: settings.regime_especial_tributacao ?? 0,
      tributacao_iss: settings.tributacao_iss ?? 1,
      codigo_tributacao_nacional_iss: settings.codigo_tributacao_nacional_iss ?? '',
      ibs_cbs_informar: settings.ibs_cbs_informar ?? false,
      percentual_tributos_simples_nacional: settings.percentual_tributos_simples_nacional != null ? String(settings.percentual_tributos_simples_nacional) : '',
      dps_serie: settings.dps_serie != null ? String(settings.dps_serie) : '1',
      dps_proximo_numero: settings.dps_proximo_numero != null ? String(settings.dps_proximo_numero) : '1',
    });
  };

  const handleFieldChange = (field) => (value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) setErrors((prev) => ({ ...prev, [field]: '' }));
  };

  const handleSwitchChange = (field) => (e) => {
    setForm((prev) => ({ ...prev, [field]: e.target.checked }));
  };

  const handleSelectChange = (field) => (e) => {
    setForm((prev) => ({ ...prev, [field]: e.target.value }));
  };

  const validate = () => {
    const newErrors = {};
    if (!form.issuer_cnpj?.trim()) newErrors.issuer_cnpj = 'CNPJ do prestador é obrigatório';
    if (!form.issuer_legal_name?.trim()) newErrors.issuer_legal_name = 'Razão social é obrigatória';
    if (!form.issuer_city_code?.trim()) newErrors.issuer_city_code = 'Código do município é obrigatório';
    if (!form.default_service_code?.trim()) newErrors.default_service_code = 'Código de serviço padrão é obrigatório';
    if (form.nfse_layout === 'nacional') {
      const codigoTribNac = String(form.codigo_tributacao_nacional_iss || '').replace(/\D/g, '');
      if (codigoTribNac.length !== 6) newErrors.codigo_tributacao_nacional_iss = 'Informe o código de tributação nacional com 6 dígitos';
      const serie = Number(form.dps_serie);
      if (!Number.isInteger(serie) || serie < 1 || serie > 49999) newErrors.dps_serie = 'Informe uma série entre 1 e 49999';
      const proximo = Number(form.dps_proximo_numero);
      if (!Number.isInteger(proximo) || proximo < 1) newErrors.dps_proximo_numero = 'Informe um número maior que zero';
      // ME/EPP: o Emissor Nacional exige o percentual aproximado dos tributos do Simples (E0712).
      if (Number(form.codigo_opcao_simples_nacional) === 3) {
        const percentual = Number(String(form.percentual_tributos_simples_nacional || '').replace(',', '.'));
        if (!(percentual > 0 && percentual < 100)) newErrors.percentual_tributos_simples_nacional = 'Informe um percentual maior que 0 e menor que 100';
      }
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = () => {
    if (!validate()) return;

    setSaving(true);
    setSuccessMsg('');
    setErrorMsg('');

    const payload = {
      enabled: form.enabled,
      provider: form.provider,
      environment: form.environment,
      issuer_cnpj: form.issuer_cnpj,
      issuer_legal_name: form.issuer_legal_name,
      issuer_municipal_registration: form.issuer_municipal_registration,
      issuer_city_code: form.issuer_city_code,
      issuer_city_name: form.issuer_city_name,
      default_service_code: form.default_service_code,
      default_service_description: form.default_service_description,
      default_aliquot: form.default_aliquot !== '' ? parseFloat(form.default_aliquot) : null,
      default_iss_withheld: form.default_iss_withheld,
      send_email_to_patient: form.send_email_to_patient,
      copy_email: form.copy_email,
      // Campos da Reforma Tributária — enviados exatamente como preenchidos pelo usuário
      item_lista_servico: form.item_lista_servico,
      codigo_nbs: form.codigo_nbs,
      codigo_indicador_operacao: form.codigo_indicador_operacao,
      ibs_cbs_classificacao_tributaria: form.ibs_cbs_classificacao_tributaria,
      natureza_operacao: form.natureza_operacao,
      // Layout de emissão e dados do Emissor Nacional; is_simples_nacional é derivado do enquadramento
      nfse_layout: form.nfse_layout,
      codigo_opcao_simples_nacional: Number(form.codigo_opcao_simples_nacional),
      is_simples_nacional: Number(form.codigo_opcao_simples_nacional) !== 1,
      regime_tributario_simples_nacional: form.regime_tributario_simples_nacional === '' ? null : Number(form.regime_tributario_simples_nacional),
      regime_especial_tributacao: Number(form.regime_especial_tributacao),
      tributacao_iss: Number(form.tributacao_iss),
      codigo_tributacao_nacional_iss: form.codigo_tributacao_nacional_iss,
      ibs_cbs_informar: form.ibs_cbs_informar,
      percentual_tributos_simples_nacional: form.percentual_tributos_simples_nacional,
      dps_serie: Number(form.dps_serie),
      dps_proximo_numero: form.dps_proximo_numero !== '' ? Number(form.dps_proximo_numero) : null,
    };

    // Só enviar api_key se foi preenchida
    if (form.api_key?.trim()) {
      payload.api_key = form.api_key;
    }

    dataProvider.SetFiscalSettings(payload, (result) => {
      setSaving(false);
      if (result?.success) {
        setSuccessMsg('Configurações fiscais salvas com sucesso!');
        setEditing(false);
        loadSettings();
      } else {
        setErrorMsg(result?.error || 'Erro ao salvar configurações fiscais.');
      }
    });
  };

  const handleCancel = () => {
    setEditing(false);
    setErrors({});
    setErrorMsg('');
    if (savedSettings) {
      populateForm(savedSettings);
    } else {
      setForm({ ...EMPTY_FORM });
    }
  };

  const renderEnvironmentWarning = () => {
    if (form.environment !== 'homologacao') return null;
    return (
      <Alert severity="warning" icon={<WarningAmberOutlinedIcon fontSize="small" />} sx={{ mt: 1.5 }}>
        <strong>Ambiente de Homologação ativo.</strong> As notas emitidas não têm validade fiscal.
        Use apenas para testes. Mude para <strong>Produção</strong> quando estiver pronto para emitir notas reais.
      </Alert>
    );
  };

  const renderStatusBadge = () => {
    if (!hasConfig) return null;
    const isEnabled = savedSettings?.enabled;
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <CheckCircleOutlineIcon
          sx={{ color: isEnabled ? ColorPalette.success || '#4caf50' : '#bbb', fontSize: 20 }}
        />
        <Typography variant="body2" color={isEnabled ? 'success.main' : 'text.secondary'}>
          {isEnabled ? 'Módulo habilitado' : 'Módulo desabilitado'}
        </Typography>
      </Box>
    );
  };

  const renderForm = () => (
    <Box>
      {/* Módulo habilitado */}
      <div className={styles.section}>
        <div className={styles.sectionTitle}>Status do Módulo</div>
        <FormControlLabel
          control={
            <Switch
              checked={form.enabled}
              onChange={handleSwitchChange('enabled')}
              color="primary"
              disabled={!editing}
            />
          }
          label={
            <Typography variant="body1">
              {form.enabled ? 'Módulo de Nota Fiscal habilitado' : 'Módulo de Nota Fiscal desabilitado'}
            </Typography>
          }
        />
      </div>

      <Divider sx={{ my: 2 }} />

      {/* Provedor e ambiente */}
      <div className={styles.section}>
        <div className={styles.sectionTitle}>Provedor Fiscal</div>
        <div className={styles.formGrid}>
          <FormControl size="small" disabled={!editing}>
            <InputLabel>Provedor</InputLabel>
            <Select
              value={form.provider}
              label="Provedor"
              onChange={handleSelectChange('provider')}
            >
              <MenuItem value="focus_nfe">Focus NFe</MenuItem>
            </Select>
          </FormControl>

          <FormControl size="small" disabled={!editing}>
            <InputLabel>Ambiente</InputLabel>
            <Select
              value={form.environment}
              label="Ambiente"
              onChange={handleSelectChange('environment')}
            >
              <MenuItem value="homologacao">Homologação (testes, sem validade fiscal)</MenuItem>
              <MenuItem value="producao">Produção (notas com validade fiscal)</MenuItem>
            </Select>
          </FormControl>
        </div>
        {renderEnvironmentWarning()}
      </div>

      <Divider sx={{ my: 2 }} />

      {/* API Key */}
      <div className={styles.section}>
        <div className={styles.sectionTitle}>Credenciais de Integração</div>
        <div className={styles.formGroup}>
          <InputField
            Label="Token da Focus NFe (API Key)"
            Value={form.api_key}
            OnUpdateValue={handleFieldChange('api_key')}
            Type="password"
            Disabled={!editing}
            placeholder={
              hasApiKey
                ? '••••••••••••••••••••••••••••••••'
                : 'Cole aqui o token fornecido pela Focus NFe'
            }
          />
          {hasApiKey ? (
            <span className={styles.apiKeyHint} style={{ color: '#4caf50' }}>
              <CheckCircleOutlineIcon className={styles.apiKeyHintIcon} />
              Chave configurada. Deixe em branco para manter a atual.
            </span>
          ) : (
            <span className={styles.apiKeyHint}>
              O token nunca é exibido após salvo por segurança.
            </span>
          )}
        </div>
      </div>

      <Divider sx={{ my: 2 }} />

      {/* Dados do prestador */}
      <div className={styles.section}>
        <div className={styles.sectionTitle}>Dados do Prestador de Serviço</div>
        <div className={styles.formGrid}>
          <div className={styles.formGroup}>
            <InputField
              Label="CNPJ do Prestador *"
              Value={form.issuer_cnpj}
              OnUpdateValue={handleFieldChange('issuer_cnpj')}
              Disabled={!editing}
              Mask="99.999.999/9999-99"
            />
            {errors.issuer_cnpj && <span className={styles.fieldError}>{errors.issuer_cnpj}</span>}
          </div>

          <div className={styles.formGroup}>
            <InputField
              Label="Razão Social *"
              Value={form.issuer_legal_name}
              OnUpdateValue={handleFieldChange('issuer_legal_name')}
              Disabled={!editing}
            />
            {errors.issuer_legal_name && <span className={styles.fieldError}>{errors.issuer_legal_name}</span>}
          </div>

          <div className={styles.formGroup}>
            <InputField
              Label="Inscrição Municipal"
              Value={form.issuer_municipal_registration}
              OnUpdateValue={handleFieldChange('issuer_municipal_registration')}
              Disabled={!editing}
            />
          </div>

          <div className={styles.formGroup}>
            <InputField
              Label="Código do Município (IBGE) *"
              Value={form.issuer_city_code}
              OnUpdateValue={handleFieldChange('issuer_city_code')}
              Disabled={!editing}
            />
            {errors.issuer_city_code && <span className={styles.fieldError}>{errors.issuer_city_code}</span>}
          </div>

          <div className={styles.formGroup}>
            <InputField
              Label="Nome do Município"
              Value={form.issuer_city_name}
              OnUpdateValue={handleFieldChange('issuer_city_name')}
              Disabled={!editing}
            />
          </div>
        </div>
      </div>

      <Divider sx={{ my: 2 }} />

      {/* Configurações padrão do serviço */}
      <div className={styles.section}>
        <div className={styles.sectionTitle}>Configurações Padrão do Serviço</div>
        <div className={styles.formGrid}>
          <div className={styles.formGroup}>
            <InputField
              Label="Código de Serviço Padrão (LC 116) *"
              Value={form.default_service_code}
              OnUpdateValue={handleFieldChange('default_service_code')}
              Disabled={!editing}
              placeholder="Ex: 14.01"
            />
            {errors.default_service_code && <span className={styles.fieldError}>{errors.default_service_code}</span>}
          </div>

          <div className={styles.formGroup}>
            <InputField
              Label="Alíquota ISS Padrão (%)"
              Value={form.default_aliquot}
              OnUpdateValue={handleFieldChange('default_aliquot')}
              Disabled={!editing}
              type="number"
              placeholder="Ex: 5.00"
            />
          </div>

          <div className={`${styles.formGroup} ${styles.formGridFull}`}>
            <InputField
              Label="Descrição Padrão do Serviço"
              Value={form.default_service_description}
              OnUpdateValue={handleFieldChange('default_service_description')}
              Disabled={!editing}
              rows={3}
            />
            <span className={styles.apiKeyHint}>
              Variáveis disponíveis: <code>{'{procedure_label}'}</code> (nome do procedimento),{' '}
              <code>{'{patient_name}'}</code> (nome do paciente),{' '}
              <code>{'{appointment_date}'}</code> (data do atendimento).{' '}
              Exemplo: <em>Prestação de serviço — {'{procedure_label}'}</em>
            </span>
          </div>

          <div className={styles.formGroup}>
            <FormControlLabel
              control={
                <Switch
                  checked={form.default_iss_withheld}
                  onChange={handleSwitchChange('default_iss_withheld')}
                  color="primary"
                  disabled={!editing}
                />
              }
              label="ISS Retido na Fonte (padrão)"
            />
          </div>
        </div>
      </div>

      <Divider sx={{ my: 2 }} />

      {/* Configurações de Serviço (NFS-e): layout de emissão, enquadramento e códigos */}
      <FiscalNfseLayoutSection
        form={form}
        errors={errors}
        editing={editing}
        onFieldChange={handleFieldChange}
        onSwitchChange={handleSwitchChange}
        onSelectChange={handleSelectChange}
      />

      <Divider sx={{ my: 2 }} />

      {/* E-mail */}
      <div className={styles.section}>
        <div className={styles.sectionTitle}>Configurações de E-mail</div>
        <div className={styles.formGrid}>
          <div className={styles.formGroup}>
            <FormControlLabel
              control={
                <Switch
                  checked={form.send_email_to_patient}
                  onChange={handleSwitchChange('send_email_to_patient')}
                  color="primary"
                  disabled={!editing}
                />
              }
              label="Enviar nota por e-mail ao paciente automaticamente"
            />
          </div>

          <div className={styles.formGroup}>
            <InputField
              Label="E-mail de Cópia (opcional)"
              Value={form.copy_email}
              OnUpdateValue={handleFieldChange('copy_email')}
              Disabled={!editing}
              type="email"
              placeholder="contabilidade@empresa.com.br"
            />
          </div>
        </div>
      </div>
    </Box>
  );

  const fiscalIcon = <ReceiptLongOutlinedIcon style={{ fontSize: 24 }} />;

  return (
    <div className={styles.mainContainer}>
      {!hasPermission(SUBSCRIPTION_PERMISSIONS.FOCUS_NFSE_INTEGRATION) && (
        <FeatureLockBanner
          type="permission"
          featureName="A emissão de NFS-e"
          planName={PROFESSIONAL_PLAN_NAME}
          onUpgradeClick={() => setUpgradeModalOpen(true)}
        />
      )}
      <PlanUpgradeModal
        isOpen={upgradeModalOpen}
        onClose={() => setUpgradeModalOpen(false)}
        featureName="A emissão de NFS-e"
        planName={PROFESSIONAL_PLAN_NAME}
        type="permission"
      />
      <div className={styles.pageHeader}>
        <AreaTitle
          Title="Configurações de Nota Fiscal"
          Icon={fiscalIcon}
          Description="Configure a integração com o provedor fiscal para emissão de NFS-e"
        />
        <div className={styles.headerActions}>
          {hasConfig && !editing && renderStatusBadge()}
          {canEdit && !editing && (
            <CustomButton
              variant="contained"
              label="Editar"
              style="secondary"
              onClick={() => {
                if (!hasPermission(SUBSCRIPTION_PERMISSIONS.FOCUS_NFSE_INTEGRATION)) {
                  setUpgradeModalOpen(true);
                } else {
                  setEditing(true);
                }
              }}
              icon={<EditOutlinedIcon />}
            />
          )}
          {editing && (
            <>
              <CustomButton
                variant="contained"
                label={saving ? 'Salvando...' : 'Salvar'}
                style="secondary"
                onClick={handleSave}
                icon={saving ? <Loading size={16} /> : <SaveOutlinedIcon />}
                enabled={!saving}
              />
              <CustomButton
                variant="outlined"
                label="Cancelar"
                style="secondary"
                onClick={handleCancel}
                icon={<CloseOutlinedIcon />}
                enabled={!saving}
              />
            </>
          )}
        </div>
      </div>

      {/* External Integration Info */}
      <ExternalIntegrationInfo
        title="Integração de Nota Fiscal via Focus NFe"
        description={[
          "A emissão automática de notas fiscais na Gabriela utiliza a plataforma parceira Focus NFe.",
          "A contratação e configuração do serviço são feitas diretamente no site da Focus NFe. Após configurar sua conta, informe os dados abaixo para habilitar a integração."
        ]}
        providerName="Focus NFe"
        providerUrl="https://focusnfe.com.br"
        warning="Os custos de emissão fiscal são cobrados diretamente pela Focus NFe."
      />

      {loading ? (
        <div className={styles.loadingContainer}>
          <Loading size={40} />
          <p>Carregando configurações fiscais...</p>
        </div>
      ) : (
        <Paper
          elevation={0}
          sx={{
            backgroundColor: ColorPalette.white,
            borderRadius: '12px',
            padding: '24px',
            boxShadow: '0 2px 4px rgba(0,0,0,0.03)',
            border: `1px solid ${ColorPalette.border}`,
          }}
        >
          {successMsg && (
            <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccessMsg('')}>
              {successMsg}
            </Alert>
          )}
          {errorMsg && (
            <Alert severity="error" sx={{ mb: 3 }} onClose={() => setErrorMsg('')}>
              {errorMsg}
            </Alert>
          )}

          {!canEdit && (
            <Alert severity="info" sx={{ mb: 3 }}>
              Apenas proprietários e administradores podem editar as configurações fiscais.
            </Alert>
          )}

          {renderForm()}
        </Paper>
      )}
    </div>
  );
}

export default FiscalSettings;
